import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Table, Button, Modal,
  Tag, Badge, Statistic, Select, Space,
  Descriptions, message, Progress, Spin, Alert
} from 'antd';
import { EyeOutlined, EditOutlined, ReloadOutlined, MergeCellsOutlined } from '@ant-design/icons';
import {
  collection, getDocs, doc, updateDoc,
  query, orderBy, serverTimestamp, addDoc, getDoc
} from 'firebase/firestore';
import { db, homeownerDb, contractorDb } from '../../services/firebase';
import { CARD_STYLE, GOLD_GRADIENT } from '../../constants/theme';

const { Option } = Select;

const normalizeCategory = (raw) => {
  if (!raw) return 'other';
  const r = raw.toLowerCase();
  if (r.includes('civil') || r.includes('structural') || r.includes('masonry')) return 'civil';
  if (r.includes('paint')) return 'painting';
  if (r.includes('plumb') || r.includes('sanitary')) return 'plumbing';
  if (r.includes('electric')) return 'electrical';
  if (r.includes('carpent') || r.includes('wood')) return 'carpentry';
  if (r.includes('tile') || r.includes('floor')) return 'tiles';
  if (r.includes('hvac') || r.includes('ac') || r.includes('ventil')) return 'hvac';
  if (r.includes('interior') || r.includes('false ceil') || r.includes('wallpaper')) return 'interior';
  if (r.includes('repair') || r.includes('maintenance') || r.includes('handyman')) return 'repair';
  if (r.includes('outdoor') || r.includes('landscape') || r.includes('garden')) return 'outdoor';
  if (r.includes('industrial') || r.includes('solar') || r.includes('cctv')) return 'industrial';
  if (r.includes('aluminum') || r.includes('glass') || r.includes('fabricat') || r.includes('grill')) return 'fabrication';
  return 'other';
};

const getLeadPrice = (budget) => {
  if (!budget) return 21;
  const b = budget.toLowerCase();
  if (b.includes('under') || (b.includes('20k') && !b.includes('50k'))) return 11;
  if (b.includes('50k') || b.includes('50,000')) return 21;
  if (b.includes('1l') || b.includes('1 l') || b.includes('lakh')) return 51;
  if (b.includes('3l') || b.includes('3 l')) return 101;
  if (b.includes('3l+') || b.includes('above')) return 201;
  return 21;
};

const CATEGORIES = {
  civil:       { label: 'Civil & Structural',      color: '#C9A84C' },
  painting:    { label: 'Painting & Finishing',     color: '#1890FF' },
  plumbing:    { label: 'Plumbing & Sanitary',      color: '#52C41A' },
  electrical:  { label: 'Electrical',               color: '#FAAD14' },
  carpentry:   { label: 'Carpentry & Woodwork',     color: '#722ED1' },
  tiles:       { label: 'Tiles & Flooring',         color: '#993556' },
  hvac:        { label: 'HVAC / AC',                color: '#1D9E75' },
  interior:    { label: 'Interior Design',          color: '#FF7A45' },
  repair:      { label: 'Repair & Maintenance',     color: '#B0B8C1' },
  outdoor:     { label: 'Outdoor & Landscaping',    color: '#52C41A' },
  industrial:  { label: 'Industrial & Specialized', color: '#534AB7' },
  fabrication: { label: 'Aluminum & Fabrication',   color: '#993C1D' },
  other:       { label: 'Other',                    color: '#7A8A96' },
};

const SOURCE_COLORS = {
  homeowner_app:  { color: '#1890FF', label: '📱 HomeOwner App' },
  website:        { color: '#52C41A', label: '🌐 Website' },
  contractor_app: { color: '#C9A84C', label: '👷 Contractor App' },
  admin:          { color: '#FAAD14', label: '🛡️ Admin' },
  sheets:         { color: '#8B5CF6', label: '📊 Google Sheets' },
};

const SOURCE_PRIORITY = { homeowner_app: 3, website: 2, contractor_app: 1, admin: 1, sheets: 0 };

export default function LeadManagement() {
  const [jobs, setJobs]                     = useState([]);
  const [commissionConfig, setCommissionConfig] = useState({});
  const [selectedJob, setSelectedJob]       = useState(null);
  const [jobModal, setJobModal]             = useState(false);
  const [capModal, setCapModal]             = useState(false);
  const [newCap, setNewCap]                 = useState(7);
  const [filterStatus, setFilterStatus]     = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSource, setFilterSource]     = useState('all');
  const [loading, setLoading]               = useState(true);
  const [actionLoading, setActionLoading]   = useState(false);
  const [sourceCounts, setSourceCounts]     = useState({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {

      // ── 1. kamsathi-admin jobs ──
      let adminJobs = [];
      try {
        const snap = await getDocs(query(collection(db, 'jobs'), orderBy('postedAt', 'desc')));
        adminJobs = snap.docs.map(d => ({
          id: 'admin_' + d.id,
          _rawId: d.id,
          _source: 'admin',
          ...d.data(),
          name:            d.data().homeownerName || d.data().name || 'N/A',
          phone:           String(d.data().homeownerPhone || d.data().whatsapp || d.data().phone || '').replace(/\D/g, '').slice(-10),
          workCategory:    d.data().workCategory || d.data().title || '',
          specificWork:    d.data().specificWork || d.data().description || '',
          budget:          d.data().budget || (d.data().budgetMin ? `Rs.${d.data().budgetMin}–${d.data().budgetMax}` : ''),
          city:            d.data().city || '',
          status:          d.data().status || 'pending',
          contractorCount: d.data().contractorCount || d.data().leadsBought || 0,
          maxLeads:        d.data().maxLeads || 7,
          timeline:        d.data().timeline || '',
          propertyType:    d.data().propertyType || '',
          areaSize:        d.data().areaSize || '',
          callTime:        d.data().callTime || '',
          notes:           d.data().notes || '',
          postedAt:        d.data().postedAt?.toDate?.()?.toLocaleDateString('en-IN')
                        || d.data().createdAt?.toDate?.()?.toLocaleDateString('en-IN') || 'N/A',
        }));
      } catch (e) { console.log('Admin jobs error:', e); }

      // ── 2. kamsathi-homeowner jobs ──
      let homeownerJobs = [];
      try {
        const snap = await getDocs(query(collection(homeownerDb, 'jobs'), orderBy('createdAt', 'desc')));
        homeownerJobs = snap.docs.map(d => ({
          id: 'homeowner_' + d.id,
          _rawId: d.id,
          _source: 'homeowner_app',
          name:            d.data().name || 'N/A',
          phone:           String(d.data().whatsapp || d.data().phone || '').replace(/\D/g, '').slice(-10),
          workCategory:    d.data().workCategory || '',
          specificWork:    d.data().specificWork || '',
          budget:          d.data().budget || '',
          city:            d.data().city || d.data().area || '',
          status:          d.data().status || 'pending',
          contractorCount: d.data().contractorCount || 0,
          maxLeads:        d.data().maxLeads || 7,
          timeline:        d.data().timeline || '',
          propertyType:    d.data().propertyType || '',
          areaSize:        d.data().areaSize || '',
          callTime:        d.data().callTime || '',
          notes:           d.data().notes || '',
          userId:          d.data().userId || '',
          postedAt:        d.data().createdAt?.toDate?.()?.toLocaleDateString('en-IN') || 'N/A',
        }));
      } catch (e) { console.log('HomeOwner jobs error:', e); }

      // ── 3. kamsathi-27dba leads ──
      let contractorLeads = [];
      try {
        const snap = await getDocs(collection(contractorDb, 'leads'));
        contractorLeads = snap.docs.map(d => ({
          id: 'contractor_' + d.id,
          _rawId: d.id,
          _source: d.data().source === 'website' ? 'website' : 'contractor_app',
          name:            d.data().name || 'N/A',
          phone:           String(d.data().phone || '').replace(/\D/g, '').slice(-10),
          workCategory:    d.data().workType || '',
          specificWork:    d.data().notes || '',
          budget:          d.data().budget || '',
          city:            d.data().area || '',
          status:          d.data().status || 'pending',
          contractorCount: d.data().unlockCount || 0,
          maxLeads:        d.data().maxUnlocks || 7,
          timeline:        d.data().timeline || '',
          propertyType:    d.data().property || '',
          areaSize:        d.data().size || '',
          callTime:        d.data().callTime || '',
          notes:           d.data().notes || '',
          postedAt:        d.data().createdAt
                            ? new Date(d.data().createdAt).toLocaleDateString('en-IN')
                            : 'N/A',
        }));
      } catch (e) { console.log('Contractor leads error:', e); }

      // ── 4. Merge & Deduplicate by phone + workCategory ──
      // Key = phone + normalized category → same person, same job type = duplicate
      // Same person, different category = kept as separate rows
      const allJobs = [...homeownerJobs, ...contractorLeads, ...adminJobs];
      const jobMap  = new Map();

      allJobs.forEach(job => {
        const cleanPhone = String(job.phone || '').replace(/\D/g, '').slice(-10);
        const cleanCat   = normalizeCategory(job.workCategory);

        // Unique key = phone + category + month-year
        // Same person, same category, same month = duplicate
        // Same person, same category, different month = kept as separate job
        const monthYear = job.postedAt && job.postedAt !== 'N/A'
          ? job.postedAt.split('/').slice(1).join('_') // e.g. "05_2026"
          : Date.now().toString(); // fallback = always unique

        const key = cleanPhone.length >= 10
          ? `${cleanPhone}_${cleanCat}_${monthYear}`
          : job.id;

        if (jobMap.has(key)) {
          const existing = jobMap.get(key);
          // Keep higher priority source; merge contractor count
          if ((SOURCE_PRIORITY[job._source] || 0) > (SOURCE_PRIORITY[existing._source] || 0)) {
            jobMap.set(key, {
              ...job,
              _cleanPhone:     cleanPhone,
              contractorCount: Math.max(job.contractorCount || 0, existing.contractorCount || 0),
              _duplicate:      true,
            });
          } else {
            jobMap.set(key, {
              ...existing,
              contractorCount: Math.max(existing.contractorCount || 0, job.contractorCount || 0),
              _duplicate:      true,
            });
          }
        } else {
          jobMap.set(key, { ...job, _cleanPhone: cleanPhone });
        }
      });

      const merged = Array.from(jobMap.values()).sort((a, b) => {
        const toDate = s => {
          if (!s || s === 'N/A') return new Date(0);
          const parts = s.split('/');
          return parts.length === 3
            ? new Date(`${parts[2]}-${parts[1]}-${parts[0]}`)
            : new Date(0);
        };
        return toDate(b.postedAt) - toDate(a.postedAt);
      });

      setJobs(merged);

      // ── 5. Source counts ──
      setSourceCounts({
        homeowner_app:  merged.filter(j => j._source === 'homeowner_app').length,
        website:        merged.filter(j => j._source === 'website').length,
        contractor_app: merged.filter(j => j._source === 'contractor_app').length,
        admin:          merged.filter(j => j._source === 'admin').length,
        duplicates:     allJobs.length - merged.length,
      });

      // ── 6. Commission config ──
      try {
        const configSnap = await getDoc(doc(db, 'commissionConfig', 'global_config'));
        if (configSnap.exists()) setCommissionConfig(configSnap.data().leads || {});
      } catch (e) {}

    } catch (error) {
      message.error('Failed to load leads data: ' + error.message);
    }
    setLoading(false);
  };

  const stats = {
    totalJobs:        jobs.length,
    activeJobs:       jobs.filter(j => j.status === 'active' || j.status === 'pending').length,
    totalLeadRevenue: jobs.reduce((sum, j) => sum + ((j.contractorCount || 0) * getLeadPrice(j.budget)), 0),
    totalLeadsSold:   jobs.reduce((sum, j) => sum + (j.contractorCount || 0), 0),
  };

  const filtered = jobs.filter(j => {
    const cat         = normalizeCategory(j.workCategory);
    const matchStatus = filterStatus   === 'all' || j.status    === filterStatus;
    const matchCat    = filterCategory === 'all' || cat          === filterCategory;
    const matchSource = filterSource   === 'all' || j._source   === filterSource;
    return matchStatus && matchCat && matchSource;
  });

  const handleCapChange = async () => {
    if (!selectedJob._rawId) return;
    setActionLoading(true);
    try {
      if (selectedJob._source === 'homeowner_app') {
        await updateDoc(doc(homeownerDb, 'jobs', selectedJob._rawId), {
          maxLeads: newCap, updatedAt: serverTimestamp(),
        });
      } else if (selectedJob._source === 'contractor_app' || selectedJob._source === 'website') {
        await updateDoc(doc(contractorDb, 'leads', selectedJob._rawId), {
          maxUnlocks: newCap,
        });
      } else {
        await updateDoc(doc(db, 'jobs', selectedJob._rawId), {
          maxLeads: newCap, updatedAt: serverTimestamp(),
        });
      }
      await addDoc(collection(db, 'auditLogs'), {
        action: 'LEAD CAP CHANGED',
        details: `Job: ${selectedJob.workCategory || selectedJob.name} | New cap: ${newCap} | Source: ${selectedJob._source}`,
        adminEmail: 'nksapcolouracademy@gmail.com',
        targetId: selectedJob._rawId, targetType: 'job',
        timestamp: serverTimestamp(),
      });
      setJobs(prev => prev.map(j => j.id === selectedJob.id ? { ...j, maxLeads: newCap } : j));
      message.success('Lead cap updated to ' + newCap);
      setCapModal(false);
    } catch (e) {
      message.error('Failed to update: ' + e.message);
    }
    setActionLoading(false);
  };

  const jobColumns = [
    {
      title: 'Homeowner',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 16,
            background: '#1890FF30', color: '#1890FF',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 'bold', fontSize: 14, flexShrink: 0,
          }}>
            {r.name?.charAt(0) || 'H'}
          </div>
          <div>
            <div style={{ fontWeight: 500, color: '#D4DCE3' }}>{r.name}</div>
            <div style={{ fontSize: 11, color: '#7A8A96' }}>{r.phone} • {r.city}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Work',
      render: (_, r) => {
        const cat = normalizeCategory(r.workCategory);
        const c   = CATEGORIES[cat] || CATEGORIES.other;
        return (
          <div>
            <Tag style={{ background: c.color + '20', color: c.color, border: 'none', borderRadius: 20, fontSize: 11, marginBottom: 2 }}>
              {r.workCategory || c.label}
            </Tag>
            {r.specificWork && (
              <div style={{ fontSize: 11, color: '#7A8A96' }}>{r.specificWork}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Budget',
      dataIndex: 'budget',
      render: b => <span style={{ color: '#C9A84C', fontSize: 12 }}>{b || '—'}</span>,
    },
    {
      title: 'Contractors',
      render: (_, r) => (
        <div>
          <div style={{ color: '#D4DCE3', fontWeight: 500 }}>
            {r.contractorCount || 0}/{r.maxLeads || 7}
          </div>
          <Progress
            percent={Math.round(((r.contractorCount || 0) / (r.maxLeads || 7)) * 100)}
            showInfo={false}
            strokeColor={(r.contractorCount || 0) >= (r.maxLeads || 7) ? '#FF4D4F' : '#C9A84C'}
            trailColor="#2A2A2A" size="small" style={{ width: 80 }}
          />
        </div>
      ),
    },
    {
      title: 'Lead Price',
      render: (_, r) => (
        <span style={{ color: '#52C41A', fontWeight: 500 }}>Rs.{getLeadPrice(r.budget)}</span>
      ),
    },
    {
      title: 'Source',
      render: (_, r) => {
        const s = SOURCE_COLORS[r._source] || { color: '#7A8A96', label: r._source };
        return (
          <Tag style={{ background: s.color + '20', color: s.color, border: 'none', borderRadius: 20, fontSize: 10 }}>
            {s.label}
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: s => {
        const colors = { pending: '#FAAD14', active: '#52C41A', completed: '#1890FF', cancelled: '#FF4D4F', verified: '#52C41A', rejected: '#FF4D4F' };
        return (
          <Badge
            status={s === 'active' || s === 'verified' ? 'success' : s === 'pending' ? 'warning' : 'error'}
            text={<span style={{ color: colors[s] || '#7A8A96', fontSize: 12 }}>
              {s?.charAt(0).toUpperCase() + s?.slice(1) || 'N/A'}
            </span>}
          />
        );
      },
    },
    {
      title: 'Posted',
      dataIndex: 'postedAt',
      render: d => <span style={{ color: '#7A8A96', fontSize: 11 }}>{d}</span>,
    },
    {
      title: 'Actions',
      render: (_, r) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} style={{ color: '#C9A84C' }}
            onClick={() => { setSelectedJob(r); setJobModal(true); }} />
          <Button type="text" icon={<EditOutlined />} style={{ color: '#B0B8C1' }}
            onClick={() => { setSelectedJob(r); setNewCap(r.maxLeads || 7); setCapModal(true); }} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Banner */}
      <Alert
        message={
          <span>
            Merged from <strong>3 sources</strong> — 
            📱 HomeOwner App ({sourceCounts.homeowner_app || 0}) • 
            🌐 Website ({sourceCounts.website || 0}) • 
            👷 Contractor App ({sourceCounts.contractor_app || 0})
            {sourceCounts.duplicates > 0 && ` • 🔗 ${sourceCounts.duplicates} same-person same-category duplicates removed`}
          </span>
        }
        type="info" showIcon
        icon={<MergeCellsOutlined />}
        style={{ marginBottom: 20, background: 'rgba(24,144,255,0.08)', border: '0.5px solid rgba(24,144,255,0.3)' }}
      />

      {/* Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { title: 'Total Jobs',       value: stats.totalJobs,       color: '#C9A84C' },
          { title: 'Active / Pending', value: stats.activeJobs,      color: '#52C41A' },
          { title: 'Total Leads Sold', value: stats.totalLeadsSold,  color: '#1890FF' },
          { title: 'Lead Revenue',     value: 'Rs.' + stats.totalLeadRevenue.toLocaleString(), color: '#FAAD14', isString: true },
        ].map((stat, i) => (
          <Col xs={12} lg={6} key={i}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
              <div style={{ fontSize: 12, color: '#7A8A96', marginBottom: 4 }}>{stat.title}</div>
              <div style={{ fontSize: stat.isString ? 20 : 28, fontWeight: 600, color: stat.color }}>{stat.value}</div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Lead Pricing */}
      <Card style={{ ...CARD_STYLE, marginBottom: 20 }} bodyStyle={{ padding: 16 }}>
        <div style={{ color: '#C9A84C', fontWeight: 500, marginBottom: 12 }}>Lead Pricing — Based on Budget</div>
        <Row gutter={[8, 8]}>
          {[
            { range: 'Under Rs.20k',    price: 11,  color: '#52C41A' },
            { range: 'Rs.20k – Rs.50k', price: 21,  color: '#1890FF' },
            { range: 'Rs.50k – Rs.1L',  price: 51,  color: '#FAAD14' },
            { range: 'Rs.1L – Rs.3L',   price: 101, color: '#C9A84C' },
            { range: 'Above Rs.3L',     price: 201, color: '#FF7A45' },
          ].map((item, i) => (
            <Col xs={12} sm={8} lg={4} key={i}>
              <div style={{ padding: '10px 12px', background: '#222', border: '0.5px solid rgba(201,168,76,0.15)', borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: '#7A8A96', marginBottom: 4 }}>{item.range}</div>
                <div style={{ color: item.color, fontWeight: 600, fontSize: 16 }}>Rs.{item.price}</div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Filters */}
      <Card style={{ ...CARD_STYLE, marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <span style={{ color: '#C9A84C', fontWeight: 500 }}>
            Job Leads — {filtered.length} showing
          </span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Select value={filterSource} onChange={setFilterSource} style={{ width: 160 }}>
              <Option value="all">All Sources</Option>
              <Option value="homeowner_app">📱 HomeOwner App</Option>
              <Option value="website">🌐 Website</Option>
              <Option value="contractor_app">👷 Contractor App</Option>
            </Select>
            <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 140 }}>
              <Option value="all">All Status</Option>
              <Option value="pending">Pending</Option>
              <Option value="active">Active</Option>
              <Option value="verified">Verified</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
            <Select value={filterCategory} onChange={setFilterCategory} style={{ width: 180 }}>
              <Option value="all">All Categories</Option>
              {Object.entries(CATEGORIES).map(([key, val]) =>
                <Option key={key} value={key}>{val.label}</Option>
              )}
            </Select>
            <Button type="text" icon={<ReloadOutlined />} style={{ color: '#C9A84C' }} onClick={loadData}>
              Refresh
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card style={CARD_STYLE} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table dataSource={filtered} columns={jobColumns} rowKey="id" pagination={{ pageSize: 10 }} />
        </Spin>
      </Card>

      {/* Job Details Modal */}
      <Modal
        title={<span style={{ color: '#C9A84C' }}>Job Details — {selectedJob?.name}</span>}
        open={jobModal} onCancel={() => setJobModal(false)} width={660}
        footer={<Button onClick={() => setJobModal(false)}>Close</Button>}
      >
        {selectedJob && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Name">{selectedJob.name}</Descriptions.Item>
            <Descriptions.Item label="Phone">{selectedJob.phone}</Descriptions.Item>
            <Descriptions.Item label="Work Category" span={2}>
              <Tag style={{
                background: (CATEGORIES[normalizeCategory(selectedJob.workCategory)]?.color || '#7A8A96') + '20',
                color: CATEGORIES[normalizeCategory(selectedJob.workCategory)]?.color || '#7A8A96',
                border: 'none', borderRadius: 20,
              }}>
                {selectedJob.workCategory || 'N/A'}
              </Tag>
            </Descriptions.Item>
            {selectedJob.specificWork && (
              <Descriptions.Item label="Specific Work" span={2}>{selectedJob.specificWork}</Descriptions.Item>
            )}
            <Descriptions.Item label="Budget">{selectedJob.budget || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Lead Price">Rs.{getLeadPrice(selectedJob.budget)}</Descriptions.Item>
            <Descriptions.Item label="City / Area">{selectedJob.city || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Timeline">{selectedJob.timeline || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Property Type">{selectedJob.propertyType || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Area Size">{selectedJob.areaSize || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Call Time">{selectedJob.callTime || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge
                status={selectedJob.status === 'active' || selectedJob.status === 'verified' ? 'success' : 'warning'}
                text={selectedJob.status}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Contractors Responded">
              {selectedJob.contractorCount || 0} / {selectedJob.maxLeads || 7}
            </Descriptions.Item>
            <Descriptions.Item label="Posted">{selectedJob.postedAt}</Descriptions.Item>
            <Descriptions.Item label="Source">
              <Tag style={{
                background: (SOURCE_COLORS[selectedJob._source]?.color || '#7A8A96') + '20',
                color: SOURCE_COLORS[selectedJob._source]?.color || '#7A8A96',
                border: 'none', borderRadius: 20,
              }}>
                {SOURCE_COLORS[selectedJob._source]?.label || selectedJob._source}
              </Tag>
            </Descriptions.Item>
            {selectedJob._duplicate && (
              <Descriptions.Item label="Note" span={2}>
                <span style={{ color: '#FAAD14', fontSize: 11 }}>
                  🔗 This lead exists in multiple sources — showing merged data (same person, same category)
                </span>
              </Descriptions.Item>
            )}
            {selectedJob.notes && (
              <Descriptions.Item label="Notes" span={2}>{selectedJob.notes}</Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* Cap Modal */}
      <Modal
        title={<span style={{ color: '#C9A84C' }}>Change Lead Cap</span>}
        open={capModal} onCancel={() => setCapModal(false)}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setCapModal(false)}>Cancel</Button>
            <Button loading={actionLoading} onClick={handleCapChange}
              style={{ background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }}>
              Update Cap
            </Button>
          </div>
        }
      >
        <div style={{ marginBottom: 16, color: '#B0B8C1', fontSize: 13 }}>
          Current cap: {selectedJob?.maxLeads || 7} | Contractors responded: {selectedJob?.contractorCount || 0}
        </div>
        <div style={{ marginBottom: 12, color: '#7A8A96', fontSize: 12 }}>
          Source: {SOURCE_COLORS[selectedJob?._source]?.label || selectedJob?._source}
        </div>
        <Select value={newCap} onChange={setNewCap} style={{ width: '100%' }}>
          {[3, 4, 5, 6, 7, 8, 10].map(n =>
            <Option key={n} value={n}>{n} contractors maximum</Option>
          )}
        </Select>
      </Modal>
    </div>
  );
}